import { ArrowDown, ArrowUp } from "@untitledui/icons";
import { useState } from "react";
import type { ArrayPath, Control, FieldValues, Path } from "react-hook-form";
import { useFieldArray, useWatch } from "react-hook-form";
import { Button } from "../base/buttons/button";
import { ReferenceRequirementModal } from "./reference-requirement-modal";
import {
	MAX_REFERENCE_REQUIREMENTS,
	type ReferenceRequirementSchema,
} from "./reference-requirement-schema";

const RequirementSummary = ({ requirement }: { requirement: ReferenceRequirementSchema }) => (
	<div className="flex min-w-0 flex-col gap-1">
		<span className="text-sm font-semibold text-primary">{requirement.label || "Untitled requirement"}</span>
		{requirement.masks.length > 0 ? (
			<div className="flex flex-wrap items-center gap-1.5 text-sm text-tertiary">
				<span>Formats:</span>
				{requirement.masks.map((mask, index) => (
					<code
						key={`${index}-${mask.value}`}
						className="rounded bg-secondary px-1 py-0.5 font-mono text-xs text-primary"
					>
						{mask.value}
					</code>
				))}
			</div>
		) : (
			<span className="text-sm text-tertiary">Any value accepted</span>
		)}
		{requirement.example && <span className="text-sm text-tertiary">Example: {requirement.example}</span>}
		{requirement.hint && <span className="line-clamp-2 text-sm text-quaternary">Hint: {requirement.hint}</span>}
	</div>
);

export interface ReferenceRequirementsFieldProps<T extends FieldValues> {
	control: Control<T>;
	name: string;
}

export const ReferenceRequirementsField = <T extends FieldValues>({ control, name }: ReferenceRequirementsFieldProps<T>) => {
	const { fields, append, update, remove, move } = useFieldArray({ control, name: name as ArrayPath<T> });
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [isAdding, setIsAdding] = useState(false);
	const requirements = (useWatch({ control, name: name as Path<T> }) ?? []) as ReferenceRequirementSchema[];
	const isFull = fields.length >= MAX_REFERENCE_REQUIREMENTS;
	const editingRequirement = editingIndex === null ? undefined : requirements[editingIndex];

	const closeModal = () => {
		setEditingIndex(null);
		setIsAdding(false);
	};

	const saveRequirement = (requirement: ReferenceRequirementSchema) => {
		if (editingIndex === null) {
			append(requirement as never);
		} else {
			update(editingIndex, requirement as never);
		}
		closeModal();
	};

	const otherLabels = requirements
		.filter((_, index) => index !== editingIndex)
		.map((requirement) => requirement.label);

	return (
		<div className="flex flex-col gap-3">
			{fields.map((field, index) => (
				<div key={field.id} className="flex items-start justify-between gap-4 rounded-lg border border-secondary p-4">
					<RequirementSummary requirement={requirements[index] ?? { label: "", masks: [], hint: "" }} />
					<div className="flex shrink-0 items-center gap-3">
						{fields.length > 1 && (
							<div className="flex items-center gap-0.5">
								<Button
									color="tertiary"
									size="sm"
									iconLeading={ArrowUp}
									aria-label={`Move ${requirements[index]?.label || "requirement"} up`}
									isDisabled={index === 0}
									onClick={() => move(index, index - 1)}
								/>
								<Button
									color="tertiary"
									size="sm"
									iconLeading={ArrowDown}
									aria-label={`Move ${requirements[index]?.label || "requirement"} down`}
									isDisabled={index === fields.length - 1}
									onClick={() => move(index, index + 1)}
								/>
							</div>
						)}
						<Button color="link-color" size="sm" onClick={() => setEditingIndex(index)}>
							Edit
						</Button>
						<Button color="link-destructive" size="sm" onClick={() => remove(index)}>
							Remove
						</Button>
					</div>
				</div>
			))}
			<Button color="secondary" size="sm" className="w-fit" isDisabled={isFull} onClick={() => setIsAdding(true)}>
				Add reference requirement
			</Button>
			{(isAdding || editingIndex !== null) && (
				<ReferenceRequirementModal
					requirement={editingRequirement}
					otherLabels={otherLabels}
					onSave={saveRequirement}
					onClose={closeModal}
				/>
			)}
		</div>
	);
};
